import { Button, DatePicker, Input, InputNumber, message, Modal, Radio } from 'antd';
import { API_ENDPOINTS, fetcher } from '../util/api';
import { handleMutation } from '../util/axiosConfig';
import useSWR from 'swr';
import { useMemo, useState } from 'react';
import useSWRMutation from 'swr/mutation';
import { useCurrency } from '../hooks/useCurrency';

interface CommissionRateResponse {
  name?: string;
  commissionType?: "FLAT" | "PERCENTAGE";
  commissionValue?: string | number;
  commissionCap?: string | number;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export default function AgentCommission() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { formatCurrency } = useCurrency();
  
  const { data: commission, isLoading, mutate } = useSWR<CommissionRateResponse>(
    API_ENDPOINTS.REFERRAL.COMMISSION_RATE,
    fetcher
  );

  const commissionData: CommissionRateResponse | undefined = useMemo(() => {
    return commission || undefined;
  }, [commission]);

  const [formData, setFormData] = useState<CommissionRateResponse>({
    name: commissionData?.name || '',
    commissionType: 'FLAT',
    commissionValue: '',
    commissionCap: '',
    effectiveFrom: '',
    effectiveTo: ''
  });

  const { trigger: setCommissionRate, isMutating: isUpdatingCommission } = useSWRMutation(
    API_ENDPOINTS.REFERRAL.COMMISSION_RATE,
    handleMutation
  );

  const { trigger: setCommissionCap, isMutating: isUpdatingCommCap } = useSWRMutation(
    API_ENDPOINTS.REFERRAL.COMMISSION_CAP,
    handleMutation
  );

  const saveCommissionRate = async () => {
    setIsSubmitting(true);

    try {
      const commissionName = formData?.name && formData?.name.length > 5
        ? formData?.name
        : commissionData?.name ?? "Referral Agent Commission"

      const { commissionCap, ...rest} = formData;

      if (formData.commissionType === 'PERCENTAGE' && commissionCap){
        await setCommissionCap({
          percentageCap: commissionCap,
        })
      }

      await setCommissionRate({
        ...rest,
        name: commissionName,
      });

      message.success("Commission rate updated successfully");
      setIsModalOpen(false);
      mutate();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Unable to update commission rate");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-dashed p-4 bg-slate-50">
      <div className="flex items-center justify-between gap-3">
        <div>
        <div className="font-medium uppercase mb-4">Current commission Value for all agents</div>
        <div className="text-lg font-bold text-primary">
            {isLoading
            ? "Loading..."
            : `${commissionData?.commissionType === "PERCENTAGE"
            ? (commission?.commissionValue ?? 0) + "%"
            : formatCurrency(commission?.commissionValue ?? 0)}`}
        </div>
        </div>
        <div className="flex items-center">
          <Button type="primary" onClick={() => setIsModalOpen(true)}>
            Set commission rate
          </Button>
        </div>
      </div>
      <Modal
        title="Set commission rate"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        centered
        width={420}
      >
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Commission Title</label>
            <Input
              value={formData.name ? formData.name : commissionData?.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full"
              placeholder="Enter commission title"
              disabled={isLoading || isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 block">Commission Type</label>
            <Radio.Group
              value={formData.commissionType}
              onChange={(e) => setFormData({...formData, commissionType: e.target.value})}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio value="FLAT">Flat</Radio>
              <Radio value="PERCENTAGE">Percentage</Radio>
            </Radio.Group>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Commission Value</label>
            <InputNumber
              value={formData.commissionValue ? Number(formData.commissionValue) : undefined}
              onChange={(value) => setFormData({...formData, commissionValue: value?.toString() || ""})}
              min={0}
              className="w-full"
              addonAfter={formData.commissionType === "PERCENTAGE" ? "%" : ""}
              placeholder={formData.commissionType === "PERCENTAGE" ? "e.g. 5" : "e.g. 100"}
              disabled={isLoading || isSubmitting}
            />
          </div>

          {formData.commissionType === 'PERCENTAGE' && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Commission Cap</label>
              <InputNumber
                value={formData.commissionCap ? Number(formData.commissionCap) : undefined}
                onChange={(value) => setFormData({...formData, commissionCap: value?.toString() || ""})}
                min={0}
                className="w-full"
                placeholder="Example: 2000"
                disabled={isLoading || isSubmitting}
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Effective</label>
            <div className="flex items-center gap-3">
                <DatePicker
                  placeholder="From"
                  onChange={(date, dateString) => setFormData({...formData, effectiveFrom: dateString.toString()})}
                  className="flex-1"
                />
                <DatePicker
                  placeholder="To"
                  onChange={(date, dateString) => setFormData({...formData, effectiveTo: dateString.toString()})}
                  className="flex-1"
                />
            </div>
          </div>

          <Button
            type="primary"
            className="w-full"
            onClick={saveCommissionRate}
            disabled={isLoading || isSubmitting || !formData.commissionValue}
            loading={isSubmitting}
          >
            Save commission rate
          </Button>
        </div>
      </Modal>
    </div>
  )
}
